import React, { useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../components/about.css";
import "../components/faq.css";
import CTA from "../components/cta";

const initialCalculator = {
  homePrice: "500000",
  downPayment: "100000",
  interestRate: "4.5",
  loanTerm: "25",
};

const steps = [
  {
    title: "Share Your Plan",
    text: "Tell us about your budget, preferred property, income profile, and purchase timeline.",
  },
  {
    title: "Prepare Documents",
    text: "We help you organise the documents banks need so your application is clear from the start.",
  },
  {
    title: "Compare Banks",
    text: "Our team liaises with lenders and brings suitable options together in one simple view.",
  },
  {
    title: "Get Pre-Approval",
    text: "We guide you through pre-approval and keep the financing path aligned with your property search.",
  },
  {
    title: "Move to Keys",
    text: "From valuation to final approval, MoU, NOC, and transfer, we help keep each step on track.",
  },
];

const benefits = [
  {
    title: "No extra cost to you",
    text: "Our mortgage advisory service is free for Nova Vision buyers. You only pay the bank for the loan, not us.",
  },
  {
    title: "One point of contact, many banks",
    text: "Instead of talking to several banks yourself, you deal with one team. We liaise with multiple lenders on your behalf and bring you suitable options.",
  },
  {
    title: "Time and energy saved",
    text: "You share your documents with us once. We organise them correctly and handle most of the back-and-forth with banks.",
  },
  {
    title: "Clear, simple explanations",
    text: "We explain interest rates, fixed vs variable terms, fees, and conditions in straightforward English.",
  },
  {
    title: "Aligned with your property deal",
    text: "Because we are also your real estate agency, we keep mortgage steps aligned with the MoU, NOC, and transfer timeline.",
  },
  {
    title: "Support for different profiles",
    text: "Whether you are a salaried expat, self-employed, or an overseas investor, we guide you on what is realistic for your situation.",
  },
];

const banks = [
  { code: "FAB.png", name: "First Abu Dhabi Bank" },
  { code: "ADCB.jpg", name: "Abu Dhabi Commercial Bank" },
  { code: "ENBD.jpg", name: "Emirates NBD" },
  { code: "DIB.jpg", name: "Dubai Islamic Bank" },
  { code: "MASHREQ.jpg", name: "Mashreq" },
  { code: "HSBC.jpg", name: "HSBC UAE" },
  { code: "RAK.jpg", name: "RAKBANK" },
  { code: "ADIB.jpg", name: "Abu Dhabi Islamic Bank" },
]; 

const faqs = [
  {
    icon: "fas fa-passport",
    question: "Do I need to live in the UAE to get a mortgage?",
    answer:
      "Some banks require UAE residency, while others have products for non-resident buyers with different conditions and down payments.",
  },
  {
    icon: "far fa-clock",
    question: "How long does pre-approval usually take?",
    answer:
      "If your documents are complete, many banks can issue a pre-approval in a few working days, although times can vary.",
  },
  {
    icon: "far fa-building",
    question: "Can I get a mortgage for both secondary and off-plan properties?",
    answer:
      "Yes, but rules may differ. Financing for off-plan properties is often more restricted than for ready properties.",
  },
  {
    icon: "fas fa-balance-scale",
    question: "What if the bank's valuation is lower than the agreed price?",
    answer:
      "You may need to increase your cash contribution or renegotiate the price. This depends on the lender's policy and your agreement with the seller.",
  },
];

function Mortgage() {
  const API = process.env.REACT_APP_API_URL || "https://nvrealestate.ae";
  const [calculator, setCalculator] = useState(initialCalculator);
  const [result, setResult] = useState({
    monthlyPayment: 0,
    loanAmount: 0,
    totalInterest: 0,
    totalPayment: 0,
  });
  const [activeFaq, setActiveFaq] = useState(null);
  const [showLeadPopup, setShowLeadPopup] = useState(false);
  const [leadForm, setLeadForm] = useState({
    agent_name: "",
    agent_lname: "",
    agent_phone: "",
    agent_email: "",
    agent_address: "",
  });
  const [leadErrors, setLeadErrors] = useState({});
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadResponse, setLeadResponse] = useState({ message: "", type: "" });

  const formatAED = (value) =>
    `AED ${Number(value || 0).toLocaleString("en-AE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCalculator((prev) => ({ ...prev, [name]: value }));
  };

  const handleCalculate = (event) => {
    event.preventDefault();

    const price = parseFloat(calculator.homePrice) || 0;
    const down = parseFloat(calculator.downPayment) || 0;
    const rate = parseFloat(calculator.interestRate) || 0;
    const years = parseFloat(calculator.loanTerm) || 0;
    const principal = Math.max(price - down, 0);
    const months = years * 12;

    if (!principal || !months) {
      setResult({
        monthlyPayment: 0,
        loanAmount: principal,
        totalInterest: 0,
        totalPayment: 0,
      });
      return;
    }

    if (rate === 0) {
      const payment = principal / months;
      setResult({
        monthlyPayment: payment,
        loanAmount: principal,
        totalPayment: payment * months,
        totalInterest: 0,
      });
      return;
    }

    const monthlyRate = rate / 100 / 12;
    const monthlyPayment =
      (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1);
    const totalPayment = monthlyPayment * months;

    setResult({
      monthlyPayment,
      loanAmount: principal,
      totalPayment,
      totalInterest: totalPayment - principal,
    });
  };

  const handleReset = () => {
    setCalculator(initialCalculator);
    setResult({
      monthlyPayment: 0,
      loanAmount: 0,
      totalInterest: 0,
      totalPayment: 0,
    });
  };

  const handleLeadChange = (event) => {
    const { name, value } = event.target;
    setLeadForm((prev) => ({ ...prev, [name]: value }));
    setLeadErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleLeadPhoneChange = (phone) => {
    setLeadForm((prev) => ({ ...prev, agent_phone: phone }));
    setLeadErrors((prev) => ({ ...prev, agent_phone: "" }));
  };

  const validateLeadForm = () => {
    const nextErrors = {};
    const phoneDigits = leadForm.agent_phone.replace(/\D/g, "");

    if (!leadForm.agent_name.trim()) nextErrors.agent_name = "First Name is required";
    if (!leadForm.agent_lname.trim()) nextErrors.agent_lname = "Last Name is required";
    if (!phoneDigits) {
      nextErrors.agent_phone = "Phone number is required";
    } else if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      nextErrors.agent_phone = "Invalid phone number";
    }
    if (!leadForm.agent_email.trim()) {
      nextErrors.agent_email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(leadForm.agent_email)) {
      nextErrors.agent_email = "Invalid email address";
    }

    setLeadErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleLeadSubmit = async (event) => {
    event.preventDefault();
    setLeadResponse({ message: "", type: "" });

    if (!validateLeadForm()) return;

    setLeadLoading(true);
    try {
      const payload = {
        proref: "Mortgage: Free Mortgage Advisory",
        current_url: window.location.href,
        ...leadForm,
      };
      const response = await axios.post(
        `${API}/api/EnquiryHandlerStatic.ashx`,
        JSON.stringify(payload),
        { headers: { "Content-Type": "application/json" } }
      );

      setLeadResponse({
        message: response.data?.message || "Message sent successfully!",
        type: "success",
      });
      setLeadForm({
        agent_name: "",
        agent_lname: "",
        agent_phone: "",
        agent_email: "",
        agent_address: "",
      });
      setLeadErrors({});
    } catch (error) {
      setLeadResponse({
        message: "Something went wrong. Please try again.",
        type: "error",
      });
    } finally {
      setLeadLoading(false);
    }
  };

  const animatedBanks = useMemo(() => [...banks, ...banks], []);
  
  const faqBackground = {
    backgroundImage:
      'radial-gradient(circle at 50% 15%, rgba(210, 224, 255, 0.48), transparent 34%), linear-gradient(180deg, rgba(247, 250, 255, 0.94) 0%, rgba(255, 255, 255, 0.88) 48%, rgba(241, 246, 255, 0.91) 100%), url("/assets/images/abu.jpg")',
  };

  return (
    <>
    
      <style
        dangerouslySetInnerHTML={{
          __html: `
            
           
            .mortgage-page {
              --mortgage-primary: #09256d;
              --mortgage-accent: #d2ad61;
              --mortgage-blue: #173fb4;
              --mortgage-ink: #172033;
              --mortgage-muted: #4a5b80;
              --mortgage-border: #dfe5ef;
              --mortgage-soft: #f4f8ff;
              color: var(--mortgage-ink);
            }

            .mortgage-wrap {
              width: min(1180px, calc(100% - 32px));
              margin: 0 auto;
              padding: 99px 0;
               padding-bottom:0px;
            }

            .mortgage-hero-panel {
              display: grid;
              grid-template-columns: minmax(0, 1.08fr) minmax(320px, 0.92fr);
              gap: 34px;
              align-items: center;
              margin-bottom: 48px;
            }

            .mortgage-eyebrow {
              display: inline-flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 16px;
              color: #000000;
              font-size: 14px;
              font-weight: 700;
              letter-spacing: .08em;
              text-transform: uppercase;
            }

            .mortgage-hero-panel h2 {
                  margin: 0 0 18px;
    color: var(--mortgage-primary);
    font-size: clamp(34px, 5vw, 39px);
    line-height: 1.2;
    letter-spacing: 0;
            }

            .mortgage-lead {
              max-width: 680px;
              margin: 0 0 24px;
              color: var(--mortgage-muted);
              font-size: 18px;
              line-height: 1.75;
            }

            .mortgage-actions {
              display: flex;
              flex-wrap: wrap;
              gap: 12px;
            }

            .mortgage-outline-btn {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              min-height: 48px;
              padding: 12px 22px;
              border: 1px solid var(--mortgage-primary) !important;
              border-radius: 6px;
              background: #fff;
              color: var(--mortgage-primary);
              font-weight: 700;
              transition: .2s ease;
            }

            .mortgage-btn-reset {
              border: 0;
              cursor: pointer;
              font: inherit;
            }

            .mortgage-outline-btn:hover {
              background: var(--mortgage-primary);
              color: #fff;
            }

            .mortgage-hero-card {
              position: relative;
              overflow: hidden;
              min-height: 420px;
              border-radius: 8px;
              background: url(images/inner-banner/Mortgage.jpg) center/cover no-repeat;
              box-shadow: 0 24px 60px rgba(18, 59, 109, .18);
            }

            .mortgage-hero-card::after {
              content: "";
              position: absolute;
              inset: 0;
              background: linear-gradient(180deg, rgba(18, 59, 109, 0), rgba(18, 59, 109, .82));
            }

            .mortgage-stat-card {
              position: absolute;
              left: 22px;
              right: 22px;
              bottom: 22px;
              z-index: 1;
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
            }

            .mortgage-stat-card span {
              display: block;
              padding: 15px 7px;
              border: 1px solid rgba(255,255,255,.24);
              border-radius: 6px;
              background: rgba(255,255,255,.12);
              color: #fff;
              font-size: 12px;
              font-weight: 700;
              text-align: center;
              backdrop-filter: blur(10px);
            }

            .mortgage-section-head {
              max-width: 760px;
              margin: 0 auto 28px;
              text-align: center;
            }

            .mortgage-section-head h2 {
              margin: 0 0 12px;
              color: var(--mortgage-primary);
              font-size: clamp(25px, 1vw, 42px)
              line-height: 1.14;
            }

            .mortgage-section-head p {
              margin: 0;
              color: var(--mortgage-muted);
              font-size: 15px;
              line-height: 1.7;
            }

            .mortgage-steps {
              display: grid;
              grid-template-columns: repeat(5, minmax(0, 1fr));
              gap: 14px;
              margin-bottom: 54px;
            }

            .mortgage-step {
              min-height: 218px;
              padding: 22px;
              border: 1px solid var(--mortgage-border);
              border-radius: 8px;
              background: #fff;
              box-shadow: 0 15px 40px rgba(39, 63, 109, 0.08);
            }

            .mortgage-step strong {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 38px;
              height: 38px;
              margin-bottom: 18px;
              border-radius: 50%;
              background: var(--mortgage-primary);
              color: #fff;
            }

            .mortgage-step h3 {
              margin: 0 0 10px;
              color: var(--mortgage-ink);
              font-size: 17px;
              line-height: 1.3;
            }

            .mortgage-step p,
            .mortgage-benefit p,
            .mortgage-faq p {
              margin: 0;
              color: var(--mortgage-muted);
              line-height: 1.68;
              font-size: 15px;
            }

            .mortgage-calculator {
              display: grid;
              grid-template-columns: 1.08fr .92fr;
              gap: 24px;
              align-items: stretch;
              margin-bottom: 58px;
            }

            .mortgage-card {
              border: 1px solid var(--mortgage-border);
              border-radius: 8px;
              background: #fff;
              box-shadow: 0 18px 48px rgba(23, 32, 51, .07);
              height: 100%;
            }

            .mortgage-form-card,
            .mortgage-result-card {
              padding: 28px;
              display: flex;
              flex-direction: column;
            }

            .mortgage-form-card h2,
            .mortgage-result-card h2 {
              margin: 0 0 10px;
              color: var(--mortgage-primary);
              font-size: 28px;
            }

            .mortgage-form-card > p,
            .mortgage-result-card > p {
              margin: 0 0 22px;
              color: var(--mortgage-muted);
              line-height: 1.7;
            }

            .mortgage-form {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 18px;
            }

            .mortgage-field {
              display: flex;
              flex-direction: column;
              gap: 8px;
            }

            .mortgage-field label {
              color: var(--mortgage-ink);
              font-size: 14px;
              font-weight: 800;
            }

            .mortgage-field input {
              width: 100%;
              height: 54px;
              border: 1px solid var(--mortgage-border);
              border-radius: 6px;
              padding: 0 15px;
              color: var(--mortgage-ink);
              background: #fbfcfe;
              font-size: 16px;
              outline: none;
            }

            .mortgage-field input:focus {
              border-color: var(--mortgage-primary);
              box-shadow: 0 0 0 4px rgba(18, 59, 109, .1);
            }

            .mortgage-button-row {
              grid-column: 1 / -1;
              display: flex;
              gap: 12px;
              flex-wrap: wrap;
              margin-top: 6px;
            }

            .mortgage-reset {
              min-height: 48px;
              padding: 12px 22px;
              border: 1px solid var(--mortgage-border);
              border-radius: 6px;
              background: #fff;
              color: var(--mortgage-primary);
              font-weight: 800;
            }

            .mortgage-result-main {
              margin-bottom: 22px;
    padding: 20px 0px 20px 18px;
    border-radius: 8px;
    background: #e8e8e8;
    color: var(--mortgage-primary);
    margin-top: 12px;
            }

            .mortgage-result-main span {
              display: block;
              margin-bottom: 8px;
              opacity: .82;
              font-size: 12px;
              font-weight: 800;
              letter-spacing: .08em;
              text-transform: uppercase;
            }

            .mortgage-result-main strong {
              display: block;
              color: var(--mortgage-primary);
              font-size: clamp(30px, 4vw, 31px);
              line-height: 1.1;
            }

            .mortgage-summary {
              display: grid;
              gap: 12px;
            }

            .mortgage-summary div {
              display: flex;
              justify-content: space-between;
              gap: 16px;
              padding: 10px 16px;
              border: 1px solid var(--mortgage-border);
              border-radius: 6px;
              background: #f7faff;
            }

            .mortgage-summary span {
              color: var(--mortgage-muted);
              font-size: 14px;
            }

            .mortgage-summary strong {
              color: var(--mortgage-ink);
              font-size: 15px;
            }

            .mortgage-note {
              margin-top: 16px;
              color: var(--mortgage-muted);
              font-size: 13px;
              line-height: 1.65;
            }

            .mortgage-guidance {
              display: grid;
              grid-template-columns: 2fr;
              gap: 24px;
              margin-bottom: 44px;
              align-items: stretch;
            }

            .mortgage-guidance-graphic {
              position: relative;
              overflow: hidden;
              min-height: 100%;
              border-radius: 14px;
              background:
                linear-gradient(180deg, rgba(9, 37, 109, .08), rgba(23, 63, 180, .12)),
                #f4f8ff;
              border: 1px solid rgba(215, 224, 239, 0.8);
              box-shadow: 0 15px 40px rgba(39, 63, 109, 0.08);
              padding: 26px;
            }

            .mortgage-guidance-graphic img {
              width: 100%;
              height: 280px;
              object-fit: cover;
              border-radius: 11px;
              margin-bottom: 20px;
            }

            .mortgage-graphic-card {
              display: grid;
              gap: 12px;
            }

            .mortgage-graphic-card div {
              display: flex;
              align-items: center;
              gap: 12px;
              padding: 14px;
              border-radius: 10px;
              background: #fff;
              color: var(--mortgage-primary);
              font-weight: 700;
              box-shadow: 0 8px 22px rgba(39, 63, 109, 0.06);
            }

            .mortgage-graphic-card i {
              color: var(--mortgage-blue);
              font-size: 22px;
            }

            .mortgage-benefits {
              display: grid;
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 18px;
            }

            .mortgage-benefit {
              padding: 24px;
              border: 1px solid var(--mortgage-border);
              border-radius: 8px;
              background: rgba(255,255,255,.96);
              box-shadow: 0 10px 28px rgba(39, 63, 109, 0.06);
            }

            .mortgage-benefit i {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 42px;
              height: 42px;
              margin-bottom: 16px;
              border-radius: 50%;
              background: rgba(200, 164, 93, .15);
              color: var(--mortgage-blue);
              font-size: 20px;
            }

            .mortgage-benefit h3 {
              margin: 0 0 10px;
              color: var(--mortgage-primary);
              font-size: 18px;
              line-height: 1.3;
            }

            .mortgage-banner {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 24px;
              margin-bottom: 58px;
              padding: 26px 30px;
              border-radius: 8px;
              background: linear-gradient(135deg, #fcfcfc, #adadad);
              color: #fff;
            }

            .mortgage-banner h2 {
              margin: 0 0 8px;
              color: #09256d;
              font-size: 26px;
            }

            .mortgage-banner p {
              margin: 0;
              color: #09256d;
              line-height: 1.65;
            }

            .mortgage-partners {
              margin-bottom: 58px;
              overflow: hidden;
              border: 1px solid var(--mortgage-border);
              border-radius: 8px;
              background: #fff;
            }

            .mortgage-bank-track {
              display: flex;
              width: max-content;
              animation: mortgage-scroll 28s linear infinite;
            }

            .mortgage-bank {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 10px;
              min-width: 190px;
              min-height: 120px;
              border-right: 1px solid var(--mortgage-border);
              color: var(--mortgage-primary);
              text-align: center;
              padding: 18px;
            }

            .mortgage-bank-logo {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 100%;
              height: 48px;
              border-radius: 10px;
              background: linear-gradient(135deg, #f7faff, #e7edff);
              color: var(--mortgage-blue);
              font-size: 18px;
              font-weight: 900;
              letter-spacing: 0;
              box-shadow: inset 0 0 0 1px #dfe7f6;
            }

            .mortgage-bank-name {
              color: #435579;
              font-size: 13px;
              font-weight: 700;
              line-height: 1.35; 
            }

            @keyframes mortgage-scroll {
              from { transform: translateX(0); }
              to { transform: translateX(-50%); }
            }

            .mortgage-popup-overlay {
              position: fixed;
              inset: 0;
              z-index: 9999;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 20px;
              background: rgba(9, 20, 47, .62);
            }

            .mortgage-popup {
              width: min(720px, 100%);
              max-height: calc(100vh - 40px);
              overflow-y: auto;
              border-radius: 14px;
              background: #fff;
              box-shadow: 0 28px 80px rgba(8, 19, 44, .28);
            }

            .mortgage-popup-head {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 18px;
              padding: 26px 28px 18px;
              border-bottom: 1px solid #e5ecf6;
            }

            .mortgage-popup-head h3 {
              margin: 0 0 6px;
              color: var(--mortgage-primary);
              font-size: 28px;
            }

            .mortgage-popup-head p {
              margin: 0;
              color: var(--mortgage-muted);
              line-height: 1.55;
            }

            .mortgage-popup-close {
              flex: 0 0 40px;
              height: 40px;
              border: 0;
              border-radius: 50%;
              background: #edf3ff;
              color: var(--mortgage-primary);
              cursor: pointer;
              font-size: 20px;
            }

            .mortgage-popup-form {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 16px;
              padding: 24px 28px 28px;
            }

            .mortgage-popup-form .ip-group.full {
              grid-column: 1 / -1;
            }

            .mortgage-popup-form .form-control,
            .mortgage-popup-form .phone-input {
              width: 100% !important;
              min-height: 52px;
              border: 1px solid #dfe5ef !important;
              border-radius: 7px !important;
            }

            .mortgage-popup-form textarea.form-control {
              padding-top: 14px;
            }

            .mortgage-popup-submit {
              grid-column: 1 / -1;
            }

            @media (max-width: 1024px) {
              .mortgage-hero-panel,
              .mortgage-calculator {
                grid-template-columns: 1fr;
              }

              .mortgage-steps,
              .mortgage-guidance,
              .mortgage-benefits {
                grid-template-columns: repeat(2, minmax(0, 1fr));
              }

              .mortgage-guidance {
                grid-template-columns: 1fr;
              }
            }

            @media (max-width: 700px) {
              .mortgage-wrap {
                width: min(100% - 22px, 1180px);
                padding: 94px 0;
                padding-bottom:0px;
              }

              .mortgage-hero-card {
                min-height: 340px;
              }

              .mortgage-stat-card,
              .mortgage-form,
              .mortgage-steps,
              .mortgage-benefits,
              .mortgage-popup-form {
                grid-template-columns: 1fr;
              }

              .mortgage-form-card,
              .mortgage-result-card {
                padding: 22px;
              }

              .mortgage-banner {
                align-items: flex-start;
                flex-direction: column;
                padding: 24px;
              }

              .mortgage-actions,
              .mortgage-button-row {
                flex-direction: column;
              }

              .mortgage-actions a,
              .mortgage-actions button,
              .mortgage-button-row button {
                width: 100%;
              }

              .mortgage-popup-form .ip-group.full,
              .mortgage-popup-submit {
                grid-column: auto;
              }
            }
          .mortgage-step p, .mortgage-benefit p, .mortgage-faq p {
    
    font-size: 13px !important;
    color:#000000;
}
    .mortgage-bank img{
    width:120px !important;}
   .nv-faq-main-mortgage { 
    grid-template-columns: 762px minmax(2, 1fr) !important; 
}
    .journey-card{
    background:#fff;
    border:1px solid #e5e9f2;
    border-radius:20px;
    padding:32px;
    transition:all .35s ease;
    position:relative;
    overflow:hidden;
}

.journey-card:hover{
    transform:translateY(-10px);
    box-shadow:0 20px 40px rgba(0,0,0,.08);
    border-color:#0d2f81;
}

.journey-card:hover .step-number{
    transform:scale(1.1);
}

.step-number{
    transition:.35s ease;
}
    .mortgage-journey{
    position:relative;
}

.mortgage-journey::before{
    content:'';
    position:absolute;
    top:55px;
    left:8%;
    right:8%;
    height:2px;
    background:#d7dff0;
    z-index:0;
}
    .step-number{
    position:relative;
    z-index:2;
}
    .journey-card::before{
    content:'';
    position:absolute;
    top:0;
    left:0;
    width:100%;
    height:4px;
    background:linear-gradient(
        90deg,
        #0d2f81,
        #2f6bff
    );
}
    .step-number{
    width:56px;
    height:56px;
    border-radius:50%;
    background:#0d2f81;
    color:#fff;
    display:flex;
    align-items:center;
    justify-content:center;
    font-weight:700;
    box-shadow:0 10px 20px rgba(13,47,129,.25);
}
          
          `,
        }}
      />

      <main className="mortgage-page">
        <div className="mortgage-wrap">
          <section className="mortgage-hero-panel">
            <div> 
              <h2>
                Free Mortgage Advisory With Every Purchase 
              </h2>
              <p className="mortgage-lead">
                Nova Vision helps you compare banks, understand your options,
                and align your mortgage with your property purchase at no extra
                cost. You get one trusted point of contact instead of chasing
                multiple lenders alone.
              </p>
              <div className="mortgage-actions">
                <button
                  type="button"
                  className="tf-btn-top primary mortgage-btn-reset"
                  onClick={() => setShowLeadPopup(true)}
                >
                  Get Free Mortgage Help
                </button>
                <button
                  type="button"
                  className="mortgage-outline-btn mortgage-btn-reset"
                  onClick={() => setShowLeadPopup(true)}
                >
                  Download Our Guide
                </button>
              </div> 
            </div>
            <div className="mortgage-hero-card" aria-label="Nova Vision mortgage advisor">
               
            </div> 
          </section>
 <section>
            <div className="mortgage-section-head">
               
              <h2>Our Partners</h2>
            
            </div>
            <div className="mortgage-partners" aria-label="UAE bank partner presentation">
              <div className="mortgage-bank-track">
                {animatedBanks.map((bank, index) => (
                  <div className="mortgage-bank" key={`${bank.code}-${index}`}>
                  <img src={"/images/"+bank.code} alt={bank.name} className="lazyloaded" /> 
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section>
            <div className="mortgage-section-head"> 
              <h2>Your Mortgage Journey in 5 Steps</h2>
              <p>
                We keep the finance journey connected to the property purchase
                so your bank approval, valuation, and transfer timeline stay
                coordinated.
              </p>
            </div>
            <div className="mortgage-steps">
              {steps.map((step, index) => (
                <article className="mortgage-step" key={step.title}>
                  <strong>{index + 1}</strong>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </section> 
          <section className="mortgage-calculator" id="mortgage-calculator">
            <div className="mortgage-card mortgage-form-card">
              <h2>Mortgage Calculator</h2>
              <p>
                Estimate your monthly payment using property price, down
                payment, interest rate, and loan term.
              </p>
              <form className="mortgage-form" onSubmit={handleCalculate}>
                <div className="mortgage-field">
                  <label htmlFor="homePrice">Property Price</label>
                  <input
                    type="number"
                    id="homePrice"
                    name="homePrice"
                    min="0"
                    value={calculator.homePrice}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mortgage-field">
                  <label htmlFor="downPayment">Down Payment</label>
                  <input
                    type="number"
                    id="downPayment"
                    name="downPayment"
                    min="0"
                    value={calculator.downPayment}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mortgage-field">
                  <label htmlFor="interestRate">Interest Rate (%)</label>
                  <input
                    type="number"
                    id="interestRate"
                    name="interestRate"
                    min="0"
                    step="0.01"
                    value={calculator.interestRate}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mortgage-field">
                  <label htmlFor="loanTerm">Loan Term (Years)</label>
                  <input
                    type="number"
                    id="loanTerm"
                    name="loanTerm"
                    min="1"
                    value={calculator.loanTerm}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mortgage-button-row">
                  <button type="submit" className="tf-btn-top primary">
                    Calculate Mortgage
                  </button>
                  <button type="button" className="mortgage-reset" onClick={handleReset}>
                    Reset
                  </button>
                </div>
              </form>
            </div>

            <aside className="mortgage-card mortgage-result-card">
              <h2>Estimated Result</h2>
               
              <div className="mortgage-result-main">
                <span>Estimated monthly payment</span>
                <strong>{formatAED(result.monthlyPayment)}</strong>
              </div>
              <div className="mortgage-summary">
                <div>
                  <span>Loan Amount</span>
                  <strong>{formatAED(result.loanAmount)}</strong>
                </div>
                <div>
                  <span>Total Interest</span>
                  <strong>{formatAED(result.totalInterest)}</strong>
                </div>
                <div>
                  <span>Total Payment</span>
                  <strong>{formatAED(result.totalPayment)}</strong>
                </div>
              </div>
              
            </aside>
          </section>

          <section>
            <div className="mortgage-section-head"> 
              <h2>Mortgage Guidance and Property Support Under One Roof</h2>
              
            </div>
            <div className="mortgage-guidance">
              
              <div className="mortgage-benefits">
                {benefits.map((benefit) => (
                  <article className="mortgage-benefit" key={benefit.title}>
                     
                    <h3>{benefit.title}</h3>
                    <p>{benefit.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="mortgage-banner" id="mortgage-guide">
            <div>
              <h2>Need a simple mortgage checklist?</h2>
              <p>
                Speak with Nova Vision and get clear guidance on documents,
                bank comparison, pre-approval, and transfer timing.
              </p>
            </div>
            <button
              type="button"
              className="tf-btn-top primary mortgage-btn-reset"
              onClick={() => setShowLeadPopup(true)}
            >
              Request the Guide
            </button>
          </section>
         </div>
         <section className="nv-faq-section mortgage-faq-section" style={faqBackground}>
            <div className="nv-faq-container">
              <header className="nv-faq-heading">
                <h2>Frequently Asked Questions</h2> 
              </header>

              <div className="nv-faq-main-mortgage">
                 
                <div className="nv-faq-accordion">
                  {faqs.map((faq, index) => {
                    const isActive = activeFaq === index;
                    return (
                      <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                        <button
                          className="nv-faq-question"
                          type="button"
                          onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                          aria-expanded={isActive}
                          aria-controls={`mortgage-faq-answer-${index}`}
                        >
                          <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                          <strong>{faq.question}</strong>
                          <span className="nv-faq-toggle" aria-hidden="true" />
                        </button>
                        <div
                          className="nv-faq-answer"
                          id={`mortgage-faq-answer-${index}`}
                          aria-hidden={!isActive}
                        >
                          <p>{faq.answer}</p>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>

              <div className="nv-faq-trust-strip">
                <div className="nv-faq-trust-item">
                  <i className="fas fa-university" />
                  <div>
                    <strong>Multiple Banks</strong>
                    <span>Compared Clearly</span>
                  </div>
                </div>
                <div className="nv-faq-trust-item">
                  <i className="far fa-file-alt" />
                  <div>
                    <strong>Document Support</strong>
                    <span>Prepared Correctly</span>
                  </div>
                </div>
                <div className="nv-faq-trust-item">
                  <i className="fas fa-key" />
                  <div>
                    <strong>Purchase Aligned</strong>
                    <span>Until Transfer</span>
                  </div>
                </div>
                <div className="nv-faq-trust-item">
                  <i className="fas fa-headset" />
                  <div>
                    <strong>Free Advisory</strong>
                    <span>For NV Buyers</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
      </main>

      {showLeadPopup && (
        <div className="mortgage-popup-overlay" role="dialog" aria-modal="true" aria-labelledby="mortgage-popup-title">
          <div className="mortgage-popup">
            <div className="mortgage-popup-head">
              <div>
                <h3 id="mortgage-popup-title">Get Free Mortgage Help</h3>
                <p>Leave your details and our advisor will contact you about mortgage options.</p>
              </div>
              <button
                type="button"
                className="mortgage-popup-close"
                onClick={() => setShowLeadPopup(false)}
                aria-label="Close mortgage enquiry form"
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <form className="mortgage-popup-form" onSubmit={handleLeadSubmit} noValidate>
              <div className="ip-group">
                <input
                  type="text"
                  name="agent_name"
                  value={leadForm.agent_name}
                  onChange={handleLeadChange}
                  placeholder="First Name"
                  className={`form-control ${leadErrors.agent_name ? "input-error" : ""}`}
                />
                {leadErrors.agent_name && <p className="error-text">{leadErrors.agent_name}</p>}
              </div>
              <div className="ip-group">
                <input
                  type="text"
                  name="agent_lname"
                  value={leadForm.agent_lname}
                  onChange={handleLeadChange}
                  placeholder="Last Name"
                  className={`form-control ${leadErrors.agent_lname ? "input-error" : ""}`}
                />
                {leadErrors.agent_lname && <p className="error-text">{leadErrors.agent_lname}</p>}
              </div>
              <div className="ip-group">
                <PhoneInput
                  country="ae"
                  value={leadForm.agent_phone}
                  onChange={handleLeadPhoneChange}
                  inputClass="form-control phone-input"
                  containerClass="phone-container"
                  enableSearch
                  placeholder="Enter phone number"
                />
                {leadErrors.agent_phone && <p className="error-text">{leadErrors.agent_phone}</p>}
              </div>
              <div className="ip-group">
                <input
                  type="email"
                  name="agent_email"
                  value={leadForm.agent_email}
                  onChange={handleLeadChange}
                  placeholder="Email"
                  className={`form-control ${leadErrors.agent_email ? "input-error" : ""}`}
                />
                {leadErrors.agent_email && <p className="error-text">{leadErrors.agent_email}</p>}
              </div>
              <div className="ip-group full">
                <textarea
                  name="agent_address"
                  rows={4}
                  placeholder="Message"
                  value={leadForm.agent_address}
                  onChange={handleLeadChange}
                  className="form-control"
                />
              </div>
              {leadResponse.message && (
                <div
                  className={leadResponse.type === "success" ? "success-text mortgage-popup-submit" : "error-text mortgage-popup-submit"}
                  role="status"
                >
                  {leadResponse.message}
                </div>
              )}
              <button
                type="submit"
                className="tf-btn-1 btn-view primary hover-btn-view w-100 mortgage-popup-submit"
                disabled={leadLoading}
              >
                {leadLoading ? "Sending..." : "Submit Enquiry"}
              </button>
            </form>
          </div>
        </div>
      )}

      <CTA />
    </>
  );
}

export default Mortgage;
